import React, { FC, useContext } from "react";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { ShiftSettingsSection } from "@/app/application/admin-menu/settings/ShiftSettingsSection";
import { ShiftSettingsValidation } from "@/app/application/admin-menu/settings/validation/ShiftSettingsValidation";
import { ShiftSystem } from "@/features/shift/model/Shift/ShiftConfiguration";
import CustomFieldset from "@/shared/forms/CustomFieldset";
import CustomInput from "@/shared/forms/CustomInput";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { Spinner } from "@/shared/ui/Spinner";
import LinkIcon from "@mui/icons-material/Link";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import {
	Box,
	Button,
	Chip,
	Grid,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";

import { ShiftSettingsRequest } from "@/model/Application/Settings/RequestTypes";

import { useMutateUpdateShiftSettings } from "@/service/Settings/Mutations/ShiftSettingsMutations";
import {
	useFetchShiftGroups,
	useFetchShiftSettings,
} from "@/service/ShiftHomeQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


interface ShiftGeneralSettingsProps {
	shiftSystems: ShiftSystem[];
}

export const ShiftGeneralSettings: FC<ShiftGeneralSettingsProps> = ({
	shiftSystems,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const formId = "shift-general-settings-form";
	const { data: shiftSettings, isLoading: isLoadingShiftSettings } =
		useFetchShiftSettings();
	const { data: shiftGroups, isLoading: isLoadingShiftGroups } =
		useFetchShiftGroups();
	const updateShiftSettings = useMutateUpdateShiftSettings();

	const handleSubmit = async (
		values: ShiftSettingsRequest,
		formikHelpers: FormikHelpers<ShiftSettingsRequest>,
	) => {
		try {
			await updateShiftSettings(values);
			AlertRequest.success();
		} catch (e) {
			console.error(e);
			AlertRequest.failed();
		} finally {
			formikHelpers.setSubmitting(false);
		}
	};

	if (
		isLoadingShiftSettings ||
		isLoadingShiftGroups ||
		!shiftSettings ||
		!shiftGroups
	) {
		return <Spinner text={GetLanguageValueAsString(translation, "Auto.App.Application.AdminMenu.Settings.ShiftGeneralSettings.L69C24.LoadingSettings", "Loading settings")} />;
	}

	return (
		<Box>
			<Formik
				initialValues={{
					DefaultSystemId: shiftSettings.DefaultSystemId ?? null,
					DefaultGroupId: shiftSettings.DefaultGroupId ?? null,
					DefaultShiftId: shiftSettings.DefaultShiftId ?? null,
					ExternalLinkOne: shiftSettings.ExternalLinkOne,
					ExternalLinkTwo: shiftSettings.ExternalLinkTwo,
					ExternalLinkThree: shiftSettings.ExternalLinkThree,
				}}
				onSubmit={handleSubmit}
				validationSchema={ShiftSettingsValidation}
			>
				{({ values, setFieldValue, isSubmitting }) => (
					<Form id={formId}>
						<ShiftSettingsSection
							title={
								<Translate String="Settings.Shift.DefaultSettings">
									Default Settings
								</Translate>
							}
							icon={<SettingsApplicationsIcon />}
							action={
								<Button
									type="submit"
									form={formId}
									variant="outlined"
									color="inherit"
									disabled={isSubmitting}
									sx={{
										height: 32,
										minHeight: 32,
										minWidth: 82,
										px: 1.5,
										textTransform: "none",
										fontWeight: 600,
										borderColor: "divider",
										bgcolor: "background.paper",
										boxShadow: "none",
										whiteSpace: "nowrap",
									}}
								>
									Save
								</Button>
							}
							sx={{ width: "100%" }}
							headerSx={{
								alignItems: "center",
								pb: 2,
								borderBottom: (currentTheme) =>
									`1px solid ${currentTheme.palette.divider}`,
								mb: 2.5,
							}}
						>
							<Box
								sx={{
									mb: 2.5,
									p: 1.5,
									borderRadius: 2,
									border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
									bgcolor: alpha(theme.palette.primary.main, 0.03),
								}}
							>
								<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
									<Chip
										size="small"
										variant="outlined"
										label={`${shiftSystems.length} systems`}
									/>
									<Chip
										size="small"
										variant="outlined"
										label={`${Array.isArray(shiftGroups) ? shiftGroups.length : 0} groups`}
									/>
								</Stack>
							</Box>
							<Grid container spacing={2}>
								<Grid item xs={12} md={6}>
									<Box
										sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
									>
										<Typography variant="body2" color="text.secondary">
											<Translate String="Settings.Shift.DefaultSystem">
												Default System For Shift Event
											</Translate>
										</Typography>
										<Box
											component="select"
											value={values.DefaultSystemId ?? ""}
											onChange={(e) => {
												void setFieldValue(
													"DefaultSystemId",
													e.target.value ? Number(e.target.value) : null,
												);
											}}
											sx={{
												height: 40,
												px: 1.5,
												borderRadius: 1.5,
												border: (theme) => `1px solid ${theme.palette.divider}`,
												bgcolor: (theme) =>
													theme.palette.mode === "dark"
														? "rgba(0,0,0,0.2)"
														: "rgba(0,0,0,0.03)",
												color: "text.primary",
												fontSize: "0.875rem",
												"&:focus": {
													outline: "none",
													borderColor: "primary.main",
												},
											}}
										>
											<option value="">
												<Translate String="Common.Select">Select</Translate>
											</option>
											{shiftSystems.map((system) => (
												<option key={system.Id} value={system.Id}>
													{system.Name}
												</option>
											))}
										</Box>
									</Box>
								</Grid>
								<Grid item xs={12} md={6}>
									<Box
										sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
									>
										<Typography variant="body2" color="text.secondary">
											<Translate String="Settings.Shift.DefaultGroup">
												Default Group for Shift Event
											</Translate>
										</Typography>
										<Box
											component="select"
											value={values.DefaultGroupId ?? ""}
											onChange={(e) => {
												void setFieldValue(
													"DefaultGroupId",
													e.target.value ? Number(e.target.value) : null,
												);
											}}
											sx={{
												height: 40,
												px: 1.5,
												borderRadius: 1.5,
												border: (theme) => `1px solid ${theme.palette.divider}`,
												bgcolor: (theme) =>
													theme.palette.mode === "dark"
														? "rgba(0,0,0,0.2)"
														: "rgba(0,0,0,0.03)",
												color: "text.primary",
												fontSize: "0.875rem",
												"&:focus": {
													outline: "none",
													borderColor: "primary.main",
												},
											}}
										>
											<option value="">
												<Translate String="Common.Select">Select</Translate>
											</option>
											{Array.isArray(shiftGroups) &&
												shiftGroups.map((group) => (
													<option key={group.Id} value={group.Id}>
														{group.Name}
													</option>
												))}
										</Box>
									</Box>
								</Grid>
							</Grid>
						</ShiftSettingsSection>

						<Box sx={{ mt: 2 }}>
							<ShiftSettingsSection
								title={
									<Translate String="Settings.Shift.ExternalLinks">
										External Links
									</Translate>
								}
								icon={<LinkIcon />}
								action={
									<Button
										type="submit"
										form={formId}
										variant="outlined"
										color="inherit"
										disabled={isSubmitting}
										sx={{
											height: 32,
											minHeight: 32,
											minWidth: 82,
											px: 1.5,
											textTransform: "none",
											fontWeight: 600,
											borderColor: "divider",
											bgcolor: "background.paper",
											boxShadow: "none",
											whiteSpace: "nowrap",
										}}
									>
										Save
									</Button>
								}
								sx={{ width: "100%" }}
								headerSx={{
									alignItems: "center",
									pb: 2,
									borderBottom: (currentTheme) =>
										`1px solid ${currentTheme.palette.divider}`,
									mb: 2.5,
								}}
							>
								<Grid container spacing={2}>
									<Grid item xs={12} md={6} lg={4}>
										<Box
											sx={{
												p: 1.5,
												borderRadius: 2,
												border: (currentTheme) =>
													`1px solid ${currentTheme.palette.divider}`,
												bgcolor: "background.paper",
											}}
										>
											<CustomFieldset
												legend="Link 1"
												translationString="Settings.Shift.Link1"
											>
												<CustomInput
													label="Url"
													name="ExternalLinkOne.Url"
													type="url"
													id="externalLinkOne.Url"
													translationString="Settings.Shift.Url"
													fullWidth
												/>
												<CustomInput
													label="Name"
													name="ExternalLinkOne.Name"
													type="text"
													id="externalLinkOne.Name"
													translationString="Settings.Shift.Name"
													fullWidth
												/>
											</CustomFieldset>
										</Box>
									</Grid>
									<Grid item xs={12} md={6} lg={4}>
										<Box
											sx={{
												p: 1.5,
												borderRadius: 2,
												border: (currentTheme) =>
													`1px solid ${currentTheme.palette.divider}`,
												bgcolor: "background.paper",
											}}
										>
											<CustomFieldset
												legend="Link 2"
												translationString="Settings.Shift.Link2"
											>
												<CustomInput
													label="Url"
													name="ExternalLinkTwo.Url"
													type="url"
													id="externalLinkTwo.Url"
													translationString="Settings.Shift.Url"
													fullWidth
												/>
												<CustomInput
													label="Name"
													name="ExternalLinkTwo.Name"
													type="text"
													id="externalLinkTwo.Name"
													translationString="Settings.Shift.Name"
													fullWidth
												/>
											</CustomFieldset>
										</Box>
									</Grid>
									<Grid item xs={12} md={6} lg={4}>
										<Box
											sx={{
												p: 1.5,
												borderRadius: 2,
												border: (currentTheme) =>
													`1px solid ${currentTheme.palette.divider}`,
												bgcolor: "background.paper",
											}}
										>
											<CustomFieldset
												legend="Link 3"
												translationString="Settings.Shift.Link3"
											>
												<CustomInput
													label="Url"
													name="ExternalLinkThree.Url"
													type="url"
													id="externalLinkThree.Url"
													translationString="Settings.Shift.Url"
													fullWidth
												/>
												<CustomInput
													label="Name"
													name="ExternalLinkThree.Name"
													type="text"
													id="externalLinkThree.Name"
													translationString="Settings.Shift.Name"
													fullWidth
												/>
											</CustomFieldset>
										</Box>
									</Grid>
								</Grid>
							</ShiftSettingsSection>
						</Box>
					</Form>
				)}
			</Formik>
		</Box>
	);
};
